import React, { useState, useMemo, useEffect, useCallback, useContext } from "react";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import {
  Container,
  Typography,
  Paper,
  Box,
  Tabs,
  Tab,
  Grid,
  Button,
  TextField,
  InputAdornment,
  Drawer,
  IconButton,
  Chip,
  useMediaQuery,
  useTheme,
  ButtonGroup,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import HistoryIcon from "@mui/icons-material/History";
import ViewModuleIcon from "@mui/icons-material/ViewModule";
import AutoStoriesIcon from "@mui/icons-material/AutoStories";
import BookmarkBorderIcon from "@mui/icons-material/BookmarkBorder";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { getBookName, useEdition } from "./bible/edition";
import { useTheme as useAppTheme } from "./theme/ThemeContext";
import { searchBible } from "./bible/searchBible";
import SearchResults from "./components/SearchResults";
import {
  BIBLE_CATEGORIES_ZH,
  BIBLE_CATEGORIES_EN,
  getBookAbbreviation,
  filterBooksByCategory,
} from "./bible/bibleCategories";
import { getReadChapters } from "./components/ChapterProgress/useChapterProgress";
import ContinueReadingCard from "./components/Home/ContinueReadingCard";
import DailyVerseCard from "./components/Home/DailyVerseCard";
import ReadingStatsCard from "./components/Home/ReadingStatsCard";
import ChapterMatrixPane from "./components/Home/ChapterMatrixPane";
import MyHighlightsView from "./components/Home/MyHighlightsView";
import PanoramicCanonView from "./components/Home/PanoramicCanonView";
import { AuthContext } from "./firebase/authContext";
import { EVENT_READING_USER_CHANGED } from "./bible/readingStorageScope";
import {
  clearSearchHistory,
  getSearchHistory,
  recordSearchTerm,
} from "./bible/searchHistoryStorage";

const Home = () => {
  const auth = useContext(AuthContext);
  const waitingForAuthoritative = Boolean(auth && !auth.readingReady);
  const edition = useEdition();
  const ui = edition.ui;
  const isEn = edition.id === "en";
  const theme = useTheme();
  const { contentWidth = 80 } = useAppTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();

  const [searchTerm, setSearchTerm] = useState(() => searchParams.get("q") || "");
  const [lastSearchTerm, setLastSearchTerm] = useState(
    () => searchParams.get("q") || ""
  );
  const [isFromSearch, setIsFromSearch] = useState(
    () => searchParams.get("from") === "search"
  );
  const [selectedMainTab, setSelectedMainTab] = useState(0); // 0: OT, 1: NT, 2: Highlights
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [selectedBook, setSelectedBook] = useState(null);
  const [openMobileDrawer, setOpenMobileDrawer] = useState(false);
  const [homeViewMode, setHomeViewMode] = useState("matrix"); // 'matrix' | 'panoramic'
  const [bibleChapters, setBibleChapters] = useState([]);
  const [chapterTitles, setChapterTitles] = useState({});
  const [page, setPage] = useState(1);
  const [readChapterMap, setReadChapterMap] = useState(() =>
    waitingForAuthoritative ? {} : getReadChapters()
  );
  const [highlightsMap, setHighlightsMap] = useState({});
  const [searchHistory, setSearchHistory] = useState(() =>
    waitingForAuthoritative ? [] : getSearchHistory()
  );

  // Sync state with location / search parameters
  useEffect(() => {
    if (!location.search) {
      setIsFromSearch(false);
    } else {
      const fromParam = searchParams.get("from");
      if (fromParam === "search") {
        setIsFromSearch(true);
      }
      const qParam = searchParams.get("q");
      if (qParam && !searchTerm) {
        setLastSearchTerm(qParam);
      }
    }
  }, [location.search, searchParams, searchTerm]);

  // Sync read status and highlights
  const syncLocalData = useCallback(() => {
    setReadChapterMap(getReadChapters());
    try {
      const rawHighlights = localStorage.getItem("bible-verse-highlights");
      setHighlightsMap(rawHighlights ? JSON.parse(rawHighlights) : {});
    } catch (e) {
      setHighlightsMap({});
    }
  }, []);

  useEffect(() => {
    if (waitingForAuthoritative) {
      setReadChapterMap({});
      return undefined;
    }
    syncLocalData();
    window.addEventListener("bible_read_status_changed", syncLocalData);
    return () => {
      window.removeEventListener("bible_read_status_changed", syncLocalData);
    };
  }, [syncLocalData, waitingForAuthoritative]);

  const reloadSearchHistory = useCallback(() => {
    setSearchHistory(getSearchHistory());
  }, []);

  useEffect(() => {
    if (waitingForAuthoritative) {
      setSearchHistory([]);
      return undefined;
    }
    reloadSearchHistory();
    window.addEventListener(EVENT_READING_USER_CHANGED, reloadSearchHistory);
    return () => {
      window.removeEventListener(EVENT_READING_USER_CHANGED, reloadSearchHistory);
    };
  }, [reloadSearchHistory, waitingForAuthoritative]);

  // Load Bible Chapters and Titles
  useEffect(() => {
    let cancelled = false;
    setBibleChapters([]);
    const qParam = searchParams.get("q");
    const fromParam = searchParams.get("from");
    if (!qParam || fromParam === "search") {
      setSearchTerm("");
    }
    setPage(1);
    setSelectedMainTab(0);
    setSelectedCategory("all");

    Promise.all([edition.loadChapters(), edition.loadTitles()]).then(
      ([chapters, titlesModule]) => {
        if (!cancelled) {
          setBibleChapters(chapters);
          setChapterTitles(titlesModule.chapterTitles || {});

          // Default selected book or book from URL search parameters
          if (chapters && chapters.length > 0) {
            const bookParam = searchParams.get("book");
            if (bookParam && chapters.some((c) => getBookName(c) === bookParam)) {
              setSelectedBook(bookParam);
              const allUnique = Array.from(new Set(chapters.map(getBookName)));
              if (allUnique.slice(39).includes(bookParam)) {
                setSelectedMainTab(1);
              }
            } else {
              setSelectedBook(getBookName(chapters[0]));
            }
          }
        }
      }
    );
    return () => {
      cancelled = true;
    };
  }, [edition, searchParams]);

  // All unique books in canonical order
  const books = useMemo(() => {
    const bookSet = new Set();
    bibleChapters.forEach((chapter) => {
      bookSet.add(getBookName(chapter));
    });
    return Array.from(bookSet);
  }, [bibleChapters]);

  const oldTestamentBooks = useMemo(() => books.slice(0, 39), [books]);
  const newTestamentBooks = useMemo(() => books.slice(39), [books]);

  // Category definitions for current language
  const categories = isEn ? BIBLE_CATEGORIES_EN : BIBLE_CATEGORIES_ZH;
  const currentCategories =
    selectedMainTab === 0 ? categories.ot : categories.nt;

  // Filtered books based on category
  const displayedBooks = useMemo(() => {
    const baseList =
      selectedMainTab === 0 ? oldTestamentBooks : newTestamentBooks;
    const testament = selectedMainTab === 0 ? "ot" : "nt";
    return filterBooksByCategory(baseList, selectedCategory, testament, isEn);
  }, [
    isEn,
    newTestamentBooks,
    oldTestamentBooks,
    selectedCategory,
    selectedMainTab,
  ]);

  // If selectedBook is not in displayedBooks on desktop, auto select first
  useEffect(() => {
    if (
      !isMobile &&
      displayedBooks.length > 0 &&
      (!selectedBook || !displayedBooks.includes(selectedBook))
    ) {
      setSelectedBook(displayedBooks[0]);
    }
  }, [displayedBooks, isMobile, selectedBook]);

  // Search Results
  const searchResults = useMemo(() => {
    if (!searchTerm.trim()) return null;
    return searchBible({
      chapters: bibleChapters,
      term: searchTerm,
      usesPinyin: edition.usesPinyin,
      chapterTitles,
    });
  }, [bibleChapters, chapterTitles, edition.usesPinyin, searchTerm]);

  // Save search keyword to history
  const handleRecordSearchTerm = (term) => {
    if (!term || !term.trim()) return;
    setSearchHistory(recordSearchTerm(term));
  };

  const handleSearchChange = (value) => {
    setSearchTerm(value);
    setPage(1);
    if (isFromSearch) {
      setIsFromSearch(false);
      setSearchParams(value.trim() ? { q: value.trim() } : {});
    }
  };

  const applySearchExample = (value) => {
    setSearchTerm(value);
    setPage(1);
    setIsFromSearch(false);
    setSearchParams({ q: value });
    handleRecordSearchTerm(value);
  };

  const handleClearSearch = () => {
    setSearchTerm("");
    setPage(1);
    setIsFromSearch(false);
    setSearchParams({});
  };

  const handleClearHistory = () => {
    clearSearchHistory();
    setSearchHistory([]);
  };

  const handleChapterClick = (chapterId, verseNumber) => {
    const path = edition.chapterPath(chapterId);
    navigate(verseNumber ? `${path}?v=${verseNumber}` : path);
  };

  const handleBookClick = (bookName) => {
    const isNT = newTestamentBooks.includes(bookName);
    setSelectedMainTab(isNT ? 1 : 0);
    setSelectedCategory("all");
    setSelectedBook(bookName);
    setSearchTerm("");
    if (isMobile) {
      setOpenMobileDrawer(true);
    }
  };

  const handleOpenBookFromSearch = (bookName) => {
    const isNT = newTestamentBooks.includes(bookName);
    setSelectedMainTab(isNT ? 1 : 0);
    setSelectedCategory("all");
    setSelectedBook(bookName);
    const prevTerm = searchTerm.trim();
    if (prevTerm) {
      setLastSearchTerm(prevTerm);
    }
    setSearchTerm("");
    setIsFromSearch(true);
    setHomeViewMode("matrix");
    setSearchParams({
      book: bookName,
      from: "search",
      ...(prevTerm || lastSearchTerm ? { q: prevTerm || lastSearchTerm } : {}),
    });
    if (isMobile) {
      setOpenMobileDrawer(true);
    }
  };

  const handleBackToSearch = () => {
    const termToRestore = lastSearchTerm;
    setIsFromSearch(false);
    setSearchTerm(termToRestore);
    setSearchParams(termToRestore ? { q: termToRestore } : {});
  };

  const handleExitSearchDetail = () => {
    setIsFromSearch(false);
    setLastSearchTerm("");
    setSearchParams({});
  };

  // Active book chapters
  const bookChapters = useMemo(() => {
    if (!selectedBook) return [];
    return bibleChapters.filter(
      (chapter) => getBookName(chapter) === selectedBook
    );
  }, [selectedBook, bibleChapters]);

  // Book stats map: read chapter count
  const bookReadCountMap = useMemo(() => {
    const counts = {};
    bibleChapters.forEach((ch) => {
      const bName = getBookName(ch);
      if (readChapterMap[ch.id]) {
        counts[bName] = (counts[bName] || 0) + 1;
      }
    });
    return counts;
  }, [bibleChapters, readChapterMap]);

  return (
    <Container
      maxWidth={false}
      sx={{
        width: { xs: "100%", md: `${contentWidth}%` },
        maxWidth: "2200px",
        mx: "auto",
        p: {
          xs: 1,
          sm: 2,
          md: 3,
        },
        transition: "width 220ms ease",
      }}
    >
      <Paper
        elevation={0}
        sx={{
          p: {
            xs: 1.5,
            sm: 2.5,
            md: 3,
          },
          borderRadius: 3,
          border: "1px solid",
          borderColor: "divider",
          bgcolor: "background.paper",
        }}
      >
        {/* ================= 顶部标题与搜索区 ================= */}
        <Box sx={{ mb: 3 }}>
          <Box sx={{ textAlign: "center", mb: 2 }}>
            <Typography
              variant="h4"
              component="h1"
              sx={{
                color: "primary.main",
                fontWeight: 900,
                letterSpacing: { xs: "0.02em", sm: "0.04em" },
                fontSize: { xs: "1.6rem", sm: "2.1rem" },
              }}
            >
              {ui.homeTitle}
            </Typography>
            {edition.versionName && (
              <Typography
                variant="subtitle2"
                sx={{
                  color: "text.secondary",
                  mt: 0.5,
                  fontWeight: 600,
                }}
              >
                {edition.versionName}
              </Typography>
            )}
          </Box>

          {/* Search Input Bar */}
          <Box sx={{ maxWidth: 880, mx: "auto" }}>
            <TextField
              id="home-bible-search"
              name="bibleSearch"
              fullWidth
              variant="outlined"
              placeholder={ui.searchPlaceholder}
              value={searchTerm}
              inputProps={{ name: "bibleSearch", "aria-label": ui.searchPlaceholder }}
              onChange={(e) => handleSearchChange(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && searchTerm.trim()) {
                  handleRecordSearchTerm(searchTerm);
                }
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
                endAdornment: searchTerm && (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label={ui.clearSearch}
                      onClick={handleClearSearch}
                      edge="end"
                      size="small"
                      sx={{ color: "text.secondary" }}
                    >
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{
                mb: 1.5,
                "& .MuiOutlinedInput-root": {
                  borderRadius: 3,
                  bgcolor: (theme) =>
                    theme.palette.mode === "dark"
                      ? "rgba(255,255,255,0.03)"
                      : "rgba(0,0,0,0.02)",
                },
              }}
            />

            {/* Quick Search Chips & Search History */}
            <Box
              sx={{
                display: "flex",
                gap: 0.75,
                flexWrap: "wrap",
                alignItems: "center",
              }}
            >
              <Typography variant="caption" color="text.secondary">
                {ui.searchTips}
              </Typography>
              <Chip
                label={ui.tipPinyin}
                size="small"
                variant="outlined"
                onClick={() => applySearchExample(ui.suggestPinyin)}
                sx={{
                  fontSize: "0.72rem",
                  height: 22,
                  borderRadius: 1.5,
                  cursor: "pointer",
                }}
              />
              <Chip
                label={ui.tipContent}
                size="small"
                variant="outlined"
                onClick={() => applySearchExample(ui.suggestText)}
                sx={{
                  fontSize: "0.72rem",
                  height: 22,
                  borderRadius: 1.5,
                  cursor: "pointer",
                }}
              />
              <Chip
                label={ui.tipFuzzy}
                size="small"
                variant="outlined"
                onClick={() => applySearchExample(ui.suggestChapter)}
                sx={{
                  fontSize: "0.72rem",
                  height: 22,
                  borderRadius: 1.5,
                  cursor: "pointer",
                }}
              />

              {/* Recent Search History */}
              {searchHistory.length > 0 && !searchTerm && (
                <>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.25,
                      ml: 1,
                    }}
                  >
                    <HistoryIcon
                      sx={{ fontSize: 13, color: "text.secondary" }}
                    />
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontSize: "0.7rem" }}
                    >
                      {isEn ? "History:" : "历史:"}
                    </Typography>
                  </Box>
                  {searchHistory.slice(0, 3).map((item) => (
                    <Chip
                      key={item}
                      label={item}
                      size="small"
                      variant="filled"
                      onClick={() => applySearchExample(item)}
                      sx={{
                        fontSize: "0.7rem",
                        height: 20,
                        borderRadius: 1,
                        bgcolor: "action.hover",
                        cursor: "pointer",
                      }}
                    />
                  ))}
                  <Typography
                    variant="caption"
                    color="text.disabled"
                    onClick={handleClearHistory}
                    sx={{
                      fontSize: "0.68rem",
                      cursor: "pointer",
                      "&:hover": { color: "error.main" },
                    }}
                  >
                    {isEn ? "Clear" : "清空"}
                  </Typography>
                </>
              )}
            </Box>
          </Box>
        </Box>

        {/* ================= 搜索结果呈现 ================= */}
        {searchResults ? (
          searchResults.books.length +
            searchResults.headingHits.length +
            searchResults.verseHits.length ===
          0 ? (
            <Box
              sx={{
                textAlign: "center",
                py: 8,
                color: "text.secondary",
              }}
            >
              <SearchIcon sx={{ fontSize: 48, opacity: 0.5, mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                {ui.noMatchTitle}
              </Typography>
              <Typography variant="body2" sx={{ mb: 2 }}>
                {ui.noMatchHint}
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  gap: 1,
                  justifyContent: "center",
                  flexWrap: "wrap",
                  mt: 2,
                }}
              >
                <Typography variant="caption" color="text.secondary">
                  {ui.suggestions}
                </Typography>
                {[
                  ui.suggestPinyin,
                  ui.suggestBook,
                  ui.suggestChapter,
                  ui.suggestText,
                ].map((example) => (
                  <Chip
                    key={example}
                    label={example}
                    size="small"
                    variant="outlined"
                    onClick={() => applySearchExample(example)}
                    sx={{ fontSize: "0.7rem", height: "20px" }}
                  />
                ))}
              </Box>
            </Box>
          ) : (
            <SearchResults
              results={searchResults}
              term={searchTerm.trim()}
              ui={ui}
              page={page}
              onPageChange={setPage}
              onOpenBook={handleOpenBookFromSearch}
              onOpenChapter={handleChapterClick}
            />
          )
        ) : (
          /* ================= 未搜索状态：全新灵修工作台 ================= */
          <Box>
            {/* 1. 顶部工作台：继续阅读 + 今日金句 + 读经进度 (搜索进入的书卷详情页不展示继续阅读和今日经文) */}
            <Box sx={{ mb: 3.5 }}>
              <Grid container spacing={2}>
                {!isFromSearch && (
                  <>
                    <Grid item xs={12} md={7} lg={7.5}>
                      <ContinueReadingCard
                        onOpenChapter={handleChapterClick}
                        edition={edition}
                      />
                    </Grid>
                    <Grid item xs={12} md={5} lg={4.5}>
                      <DailyVerseCard
                        onOpenChapter={handleChapterClick}
                        edition={edition}
                      />
                    </Grid>
                  </>
                )}
                <Grid item xs={12}>
                  <ReadingStatsCard
                    bibleChapters={bibleChapters}
                    edition={edition}
                  />
                </Grid>
              </Grid>
            </Box>

            {/* 搜索进入的详情页提示条与快捷返回 */}
            {isFromSearch && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  mb: 2.5,
                  p: { xs: 1.25, sm: 1.5 },
                  borderRadius: 2.5,
                  bgcolor: (theme) =>
                    theme.palette.mode === "dark"
                      ? "rgba(255,255,255,0.04)"
                      : "rgba(0,0,0,0.02)",
                  border: "1px solid",
                  borderColor: "divider",
                  flexWrap: "wrap",
                  gap: 1,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    minWidth: 0,
                  }}
                >
                  <Chip
                    size="small"
                    label={isEn ? "Search Result" : "搜索结果"}
                    color="primary"
                    variant="outlined"
                    sx={{ fontWeight: 700, fontSize: "0.75rem", height: 24 }}
                  />
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 700,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {isEn
                      ? `Book: ${selectedBook}`
                      : `当前书卷：${selectedBook}`}
                    {lastSearchTerm && (
                      <Box
                        component="span"
                        sx={{
                          color: "text.secondary",
                          ml: 1,
                          fontWeight: 400,
                          display: { xs: "none", sm: "inline" },
                        }}
                      >
                        ({isEn ? `query: "${lastSearchTerm}"` : `来自搜索："${lastSearchTerm}"`})
                      </Box>
                    )}
                  </Typography>
                </Box>

                <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                  {lastSearchTerm && (
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<ArrowBackIcon />}
                      onClick={handleBackToSearch}
                      sx={{
                        textTransform: "none",
                        fontSize: "0.8rem",
                        borderRadius: 1.5,
                        px: 1.25,
                      }}
                    >
                      {isEn ? "Back to Results" : "返回搜索结果"}
                    </Button>
                  )}
                  <Button
                    size="small"
                    variant="text"
                    color="inherit"
                    onClick={handleExitSearchDetail}
                    sx={{
                      textTransform: "none",
                      fontSize: "0.8rem",
                      color: "text.secondary",
                      "&:hover": { color: "text.primary" },
                    }}
                  >
                    {isEn ? "Back to Home" : "返回主页"}
                  </Button>
                </Box>
              </Box>
            )}

            {/* 2. 主导航控制条 (旧约 / 新约 / 我的划线 + 视图切换) */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                borderBottom: "1px solid",
                borderColor: "divider",
                mb: 2.5,
                flexWrap: "wrap",
                gap: 1.5,
              }}
            >
              <Tabs
                value={selectedMainTab}
                onChange={(e, val) => {
                  setSelectedMainTab(val);
                  setSelectedCategory("all");
                }}
                sx={{
                  "& .MuiTab-root": {
                    fontWeight: 700,
                    fontSize: { xs: "0.9rem", sm: "1rem" },
                    minWidth: "auto",
                    px: { xs: 1.5, sm: 2.5 },
                  },
                }}
              >
                <Tab
                  label={
                    isEn
                      ? `Old Testament (${oldTestamentBooks.length})`
                      : `旧约全书 (${oldTestamentBooks.length}卷)`
                  }
                />
                <Tab
                  label={
                    isEn
                      ? `New Testament (${newTestamentBooks.length})`
                      : `新约全书 (${newTestamentBooks.length}卷)`
                  }
                />
                <Tab
                  icon={<BookmarkBorderIcon sx={{ fontSize: 18 }} />}
                  iconPosition="start"
                  label={isEn ? "My Highlights" : "灵修划线"}
                />
              </Tabs>

              {/* PC端视图切换开关 (双栏速查 vs 66卷全景书架) */}
              {!isMobile && selectedMainTab !== 2 && (
                <ButtonGroup size="small" variant="outlined">
                  <Button
                    variant={homeViewMode === "matrix" ? "contained" : "outlined"}
                    onClick={() => setHomeViewMode("matrix")}
                    startIcon={<AutoStoriesIcon />}
                    sx={{ textTransform: "none", fontSize: "0.8rem", px: 1.5 }}
                  >
                    {isEn ? "Book Explorer" : "书卷速查"}
                  </Button>
                  <Button
                    variant={
                      homeViewMode === "panoramic" ? "contained" : "outlined"
                    }
                    onClick={() => setHomeViewMode("panoramic")}
                    startIcon={<ViewModuleIcon />}
                    sx={{ textTransform: "none", fontSize: "0.8rem", px: 1.5 }}
                  >
                    {isEn ? "Panorama Bookshelf" : "全景书架"}
                  </Button>
                </ButtonGroup>
              )}
            </Box>

            {/* 3. 标签内容展现 */}
            {selectedMainTab === 2 ? (
              /* 我的划线面板 */
              <MyHighlightsView
                bibleChapters={bibleChapters}
                onOpenChapter={handleChapterClick}
                edition={edition}
              />
            ) : homeViewMode === "panoramic" && !isMobile ? (
              /* 全卷全景书架视图 */
              <PanoramicCanonView
                onSelectBook={(book) => {
                  setSelectedBook(book);
                  setHomeViewMode("matrix");
                }}
                edition={edition}
                readChapterMap={readChapterMap}
                bibleChapters={bibleChapters}
              />
            ) : (
              /* 书卷速查工作台 */
              <Box>
                {/* 分类过滤胶囊栏 */}
                <Box
                  sx={{
                    display: "flex",
                    gap: 1,
                    overflowX: "auto",
                    pb: 1.5,
                    mb: 2,
                    "&::-webkit-scrollbar": { height: 4 },
                    "&::-webkit-scrollbar-track": { bgcolor: "transparent" },
                    "&::-webkit-scrollbar-thumb": {
                      bgcolor: "divider",
                      borderRadius: 2,
                    },
                  }}
                >
                  {currentCategories.map((cat) => {
                    const isSelected = selectedCategory === cat.id;
                    return (
                      <Chip
                        key={cat.id}
                        label={`${cat.shortLabel || cat.label} (${cat.count})`}
                        variant={isSelected ? "filled" : "outlined"}
                        color={isSelected ? "primary" : "default"}
                        onClick={() => setSelectedCategory(cat.id)}
                        sx={{
                          fontWeight: 700,
                          fontSize: "0.8rem",
                          borderRadius: 2,
                          cursor: "pointer",
                          transition: "all 0.15s",
                          flexShrink: 0,
                          "&:hover": {
                            borderColor: "primary.main",
                          },
                        }}
                      />
                    );
                  })}
                </Box>

                {/* PC 端：现代双栏联动阅读工作台 (免弹窗) */}
                <Box
                  sx={{
                    display: { xs: "none", md: "flex" },
                    gap: 3,
                    minHeight: 520,
                  }}
                >
                  {/* 左栏：书卷磁贴列表 (320px) */}
                  <Paper
                    elevation={0}
                    sx={{
                      width: 320,
                      flexShrink: 0,
                      p: 1.5,
                      borderRadius: 3,
                      border: "1px solid",
                      borderColor: "divider",
                      bgcolor: "background.paper",
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: 800,
                        textTransform: "uppercase",
                        letterSpacing: "0.06em",
                        color: "text.secondary",
                        px: 1,
                        py: 0.5,
                        mb: 0.75,
                      }}
                    >
                      {isEn ? "Select Book" : "书卷目录"} ({displayedBooks.length})
                    </Typography>

                    <Box
                      sx={{
                        flex: 1,
                        overflowY: "auto",
                        maxHeight: "680px",
                        pr: 0.5,
                        display: "flex",
                        flexDirection: "column",
                        gap: 0.75,
                      }}
                    >
                      {displayedBooks.map((book) => {
                        const isSelected = selectedBook === book;
                        const abbr = getBookAbbreviation(book);
                        const chCount = bibleChapters.filter(
                          (c) => getBookName(c) === book
                        ).length;
                        const readCount = bookReadCountMap[book] || 0;
                        const isCompleted = chCount > 0 && readCount >= chCount;

                        return (
                          <Paper
                            key={book}
                            elevation={0}
                            onClick={() => handleBookClick(book)}
                            sx={{
                              p: 1.25,
                              px: 1.5,
                              borderRadius: 2,
                              cursor: "pointer",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              border: "1px solid",
                              borderColor: isSelected
                                ? "primary.main"
                                : "divider",
                              bgcolor: isSelected
                                ? "primary.main"
                                : isCompleted
                                ? (theme) =>
                                    theme.palette.mode === "dark"
                                      ? "rgba(34, 197, 94, 0.08)"
                                      : "rgba(34, 197, 94, 0.04)"
                                : "transparent",
                              color: isSelected
                                ? "primary.contrastText"
                                : "text.primary",
                              transition: "all 0.15s ease",
                              "&:hover": {
                                borderColor: "primary.main",
                                bgcolor: isSelected
                                  ? "primary.dark"
                                  : "action.hover",
                                transform: "translateX(2px)",
                              },
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1.25,
                                minWidth: 0,
                              }}
                            >
                              <Box
                                sx={{
                                  width: 28,
                                  height: 28,
                                  borderRadius: 1.2,
                                  bgcolor: isSelected
                                    ? "rgba(255, 255, 255, 0.25)"
                                    : "action.selected",
                                  color: isSelected ? "#fff" : "text.primary",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  fontSize: "0.75rem",
                                  fontWeight: 800,
                                  flexShrink: 0,
                                }}
                              >
                                {abbr}
                              </Box>

                              <Typography
                                variant="body2"
                                sx={{
                                  fontWeight: 700,
                                  whiteSpace: "nowrap",
                                  wordBreak: "keep-all",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                }}
                              >
                                {book}
                              </Typography>
                            </Box>

                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 0.5,
                                flexShrink: 0,
                              }}
                            >
                              {readCount > 0 && !isSelected && (
                                <Typography
                                  variant="caption"
                                  sx={{
                                    fontSize: "0.7rem",
                                    color: isCompleted
                                      ? "success.main"
                                      : "text.secondary",
                                  }}
                                >
                                  {readCount}/{chCount}
                                </Typography>
                              )}
                              <Typography
                                variant="caption"
                                sx={{
                                  fontSize: "0.72rem",
                                  opacity: isSelected ? 0.9 : 0.6,
                                }}
                              >
                                {chCount}
                                {isEn ? "ch" : "章"}
                              </Typography>
                              {isCompleted && !isSelected && (
                                <CheckCircleIcon
                                  sx={{ fontSize: 13, color: "success.main" }}
                                />
                              )}
                            </Box>
                          </Paper>
                        );
                      })}
                    </Box>
                  </Paper>

                  {/* 右栏：当前选中书卷的章节数字矩阵与纲要透视 */}
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <ChapterMatrixPane
                      selectedBook={selectedBook}
                      bookChapters={bookChapters}
                      chapterTitles={chapterTitles}
                      onOpenChapter={handleChapterClick}
                      edition={edition}
                      readChapterMap={readChapterMap}
                      highlightsMap={highlightsMap}
                    />
                  </Box>
                </Box>

                {/* 移动端：自然流畅的 3 列触控卡片网格 (彻底告别 180px 狭窄内嵌滚动框) */}
                <Box sx={{ display: { xs: "block", md: "none" } }}>
                  <Grid container spacing={1}>
                    {displayedBooks.map((book) => {
                      const abbr = getBookAbbreviation(book);
                      const chCount = bibleChapters.filter(
                        (c) => getBookName(c) === book
                      ).length;
                      const readCount = bookReadCountMap[book] || 0;
                      const isCompleted = chCount > 0 && readCount >= chCount;

                      return (
                        <Grid item xs={4} key={book}>
                          <Button
                            fullWidth
                            variant="outlined"
                            onClick={() => handleBookClick(book)}
                            sx={{
                              py: 1,
                              px: 0.75,
                              borderRadius: 2,
                              minHeight: 52,
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "center",
                              justifyContent: "center",
                              textTransform: "none",
                              borderColor: isCompleted
                                ? "success.main"
                                : "divider",
                              bgcolor: isCompleted
                                ? (theme) =>
                                    theme.palette.mode === "dark"
                                      ? "rgba(34, 197, 94, 0.08)"
                                      : "rgba(34, 197, 94, 0.04)"
                                : "background.paper",
                              transition: "all 0.15s ease",
                              "&:hover": {
                                borderColor: "primary.main",
                              },
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 0.5,
                                width: "100%",
                                justifyContent: "center",
                              }}
                            >
                              <Box
                                sx={{
                                  width: 18,
                                  height: 18,
                                  borderRadius: 0.8,
                                  bgcolor: "primary.main",
                                  color: "primary.contrastText",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  fontSize: "0.65rem",
                                  fontWeight: 800,
                                }}
                              >
                                {abbr}
                              </Box>
                              <Typography
                                variant="body2"
                                sx={{
                                  fontWeight: 700,
                                  fontSize: "0.82rem",
                                  whiteSpace: "nowrap",
                                  wordBreak: "keep-all",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  color: "text.primary",
                                }}
                              >
                                {book}
                              </Typography>
                            </Box>

                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ fontSize: "0.68rem", mt: 0.25 }}
                            >
                              {chCount}
                              {isEn ? " ch" : " 章"}
                              {readCount > 0 && ` · ${readCount}`}
                            </Typography>
                          </Button>
                        </Grid>
                      );
                    })}
                  </Grid>
                </Box>
              </Box>
            )}
          </Box>
        )}
      </Paper>

      {/* ================= 移动端底部章节选择抽屉 ================= */}
      {isMobile && (
        <Drawer
          anchor="bottom"
          open={openMobileDrawer}
          onClose={() => setOpenMobileDrawer(false)}
          PaperProps={{
            sx: {
              borderTopLeftRadius: 24,
              borderTopRightRadius: 24,
              maxHeight: "85vh",
              pb: "calc(16px + env(safe-area-inset-bottom, 0px))",
              bgcolor: "background.paper",
            },
          }}
        >
          {/* 顶部拖拽手柄 */}
          <Box
            sx={{
              width: 44,
              height: 4.5,
              bgcolor: "divider",
              borderRadius: 2,
              mx: "auto",
              mt: 1.5,
              mb: 0.5,
            }}
          />

          <Box sx={{ p: 2, height: "100%", overflowY: "auto" }}>
            <ChapterMatrixPane
              selectedBook={selectedBook}
              bookChapters={bookChapters}
              chapterTitles={chapterTitles}
              onOpenChapter={(chId) => {
                handleChapterClick(chId);
                setOpenMobileDrawer(false);
              }}
              edition={edition}
              readChapterMap={readChapterMap}
              highlightsMap={highlightsMap}
              isMobile={true}
              onClose={() => setOpenMobileDrawer(false)}
            />
          </Box>
        </Drawer>
      )}
    </Container>
  );
};

export default Home;
